import api from "@/axiosInterceptor";
import {
  QAQC_OVERVIEW_TAB_DL,
  QAQC_OVERVIEW_TAB_GAS_CCE,
  QAQC_OVERVIEW_TAB_OIL_CCE,
} from "@/components/layout/QAQC/Overview/qaqcOverviewTab.js";

const DASHBOARD_BASE_URL = "public/api/samples/qc/dashboard";

export const QAQC_DASHBOARD_TAB_ENDPOINTS = {
  [QAQC_OVERVIEW_TAB_OIL_CCE]: `${DASHBOARD_BASE_URL}/cce_oil`,
  [QAQC_OVERVIEW_TAB_GAS_CCE]: `${DASHBOARD_BASE_URL}/cce_gas`,
  [QAQC_OVERVIEW_TAB_DL]: `${DASHBOARD_BASE_URL}/dl`,
};

function parseDashboardResponse(res) {
  if (res && res.data && (res.data.statusCode === 200 || res.data.statusCode === 201)) {
    return res.data.data;
  }
  throw new Error(res?.data?.message || "Failed to fetch QA/QC dashboard");
}

export function fetchQaqcDashboardTab(tab) {
  const url = QAQC_DASHBOARD_TAB_ENDPOINTS[tab];
  if (!url) {
    return Promise.reject(new Error(`Unknown QA/QC dashboard tab: ${tab}`));
  }
  return api.get(url).then(parseDashboardResponse);
}

function hasFullDlTabPayload(dl) {
  if (!dl || typeof dl !== "object") return false;
  return (
    (Array.isArray(dl.Wells) && dl.Wells.length > 0) ||
    (Array.isArray(dl.DistributionPlots) && dl.DistributionPlots.length > 0) ||
    (Array.isArray(dl.ValidationPassRateMatrix) && dl.ValidationPassRateMatrix.length > 0)
  );
}

function mergeDlTestsFromResponse(merged, incoming, tab) {
  if (incoming?.DLTests == null) return;

  if (tab === QAQC_OVERVIEW_TAB_DL) {
    const dl = resolveTabSection(tab, incoming);
    if (dl != null) merged.DLTests = dl;
    return;
  }

  if (incoming.DLTests.Counts == null) return;
  if (hasFullDlTabPayload(merged.DLTests)) return;

  merged.DLTests = {
    ...(merged.DLTests || {}),
    Counts: incoming.DLTests.Counts,
  };
}

export function mergeDashboardApiData(existing, incoming, tab) {
  if (!incoming) return existing || null;

  const merged = existing ? { ...existing } : {};

  if (incoming.Samples != null) merged.Samples = incoming.Samples;
  if (incoming.Wells != null) merged.Wells = incoming.Wells;
  if (incoming.Tests != null) merged.Tests = incoming.Tests;

  if (tab === QAQC_OVERVIEW_TAB_OIL_CCE) {
    const oil = resolveTabSection(tab, incoming);
    if (oil != null) merged.CCETests = oil;
  } else if (tab === QAQC_OVERVIEW_TAB_GAS_CCE) {
    const gas = resolveTabSection(tab, incoming);
    if (gas != null) merged.CCETestsGas = gas;
  }

  mergeDlTestsFromResponse(merged, incoming, tab);

  return Object.keys(merged).length ? merged : null;
}

const NA = "-";

function fmtCount(v) {
  if (v === undefined || v === null || v === "") return NA;
  const n = Number(v);
  return Number.isNaN(n) ? NA : n;
}

function fmtPct(total, part) {
  if (total === undefined || total === null || total === "" || Number(total) === 0) return NA;
  const t = Number(total);
  const p = Number(part);
  if (Number.isNaN(t) || Number.isNaN(p)) return NA;
  return `${Math.round((p / t) * 100)}%`;
}

function pct(total, part) {
  if (!total || total === 0) return "0%";
  return `${Math.round((Number(part) / Number(total)) * 100)}%`;
}

function performance(passRatePct) {
  if (passRatePct >= 90) return "Good";
  if (passRatePct >= 70) return "Fair";
  return "Poor";
}

function hasTabSectionFields(section) {
  if (!section || typeof section !== "object") return false;
  return (
    section.Wells != null ||
    section.Counts != null ||
    section.DistributionPlots != null ||
    section.ValidationPassRateMatrix != null
  );
}

function resolveTabSection(tab, rawApiData) {
  if (!rawApiData || typeof rawApiData !== "object") return null;

  if (tab === QAQC_OVERVIEW_TAB_OIL_CCE) {
    if (rawApiData.CCETests != null) return rawApiData.CCETests;
    return hasTabSectionFields(rawApiData) ? rawApiData : null;
  }

  if (tab === QAQC_OVERVIEW_TAB_GAS_CCE) {
    if (rawApiData.CCETestsGas != null) return rawApiData.CCETestsGas;
    if (rawApiData.CCETests != null) return rawApiData.CCETests;
    return hasTabSectionFields(rawApiData) ? rawApiData : null;
  }

  if (tab === QAQC_OVERVIEW_TAB_DL) {
    if (rawApiData.DLTests != null) return rawApiData.DLTests;
    return hasTabSectionFields(rawApiData) ? rawApiData : null;
  }

  return null;
}

export function mapDashboardTabToView(tab, rawApiData) {
  if (!rawApiData || !tab) return null;

  const slice = {};
  if (tab === QAQC_OVERVIEW_TAB_OIL_CCE) {
    const oil = resolveTabSection(tab, rawApiData);
    if (oil == null) return null;
    slice.CCETests = oil;
  } else if (tab === QAQC_OVERVIEW_TAB_GAS_CCE) {
    const gas = resolveTabSection(tab, rawApiData);
    if (gas == null) return null;
    slice.CCETestsGas = gas;
  } else if (tab === QAQC_OVERVIEW_TAB_DL) {
    const dl = resolveTabSection(tab, rawApiData);
    if (dl == null) return null;
    slice.DLTests = dl;
  } else {
    return null;
  }

  const overview = mapDashboardToOverview(slice);
  return overview?.byTab?.[tab] ?? null;
}

export function mapDashboardToOverview(apiData) {
  if (!apiData) return null;
  const cce = apiData.CCETests || {};
  const cceCounts = cce.Counts || {};
  const cceGas = apiData.CCETestsGas || {};
  const cceGasCounts = cceGas.Counts || {};
  const dl = apiData.DLTests || {};
  const dlCounts = dl.Counts || {};

  const cceTotal = Number(cceCounts.Total) || 0;
  const dlTotal = Number(dlCounts.Total) || 0;
  const totalTestsSum = cceTotal + dlTotal;

  const ccePassed = Number(cceCounts.Passed) || 0;
  const dlPassed = Number(dlCounts.Passed) || 0;
  const passedSum = ccePassed + dlPassed;

  const cceFailed = Number(cceCounts.Failed) || 0;
  const dlFailed = Number(dlCounts.Failed) || 0;
  const failedSum = cceFailed + dlFailed;

  const ccePending = Number(cceCounts.Pending) || 0;
  const dlPending = Number(dlCounts.Pending) || 0;
  const pendingSum = ccePending + dlPending;

  const summaryStats = {
    totalTests: fmtCount(totalTestsSum),
    totalTestsChange: "",
    cceTests: fmtCount(cceCounts.Total),
    cceTestsPercent: totalTestsSum ? pct(totalTestsSum, cceCounts.Total) + " of total" : NA,
    dlTests: fmtCount(dlCounts.Total),
    dlTestsPercent: totalTestsSum ? pct(totalTestsSum, dlCounts.Total) + " of total" : NA,
    passRate: fmtPct(totalTestsSum, passedSum),
  };

  const qcStatusOverview = {
    categories: ["Passed", "Failed", "Needs Review"],
    series: [
      { name: "CCE Tests", color: "#2298D5", values: [cceCounts.Passed || 0, cceCounts.Failed || 0, cceCounts.Pending || 0] },
      { name: "DL Tests", color: "#F97316", values: [dlCounts.Passed || 0, dlCounts.Failed || 0, dlCounts.Pending || 0] },
    ],
  };

  const qcStatusPanel = {
    passed: { count: fmtCount(passedSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, passedSum) },
    needsReview: { count: fmtCount(pendingSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, pendingSum) },
    failed: { count: fmtCount(failedSum), total: fmtCount(totalTestsSum), percent: fmtPct(totalTestsSum, failedSum) },
    subtitle: "Current QC validation results",
  };

  function wellsToScorecardRows(wells) {
    if (!Array.isArray(wells)) return [];
    return wells.map((w) => {
      const c = w.Counts || {};
      const qc = w.QualityCounts || {};
      const rawSamples = Array.isArray(w.Samples) ? w.Samples : [];
      const sampleEntries = rawSamples.map((s) => ({
        id: s.ID != null ? String(s.ID) : "",
        fluidSampleID: s.FluidSampleID != null ? String(s.FluidSampleID) : "",
        reportIdentifier:
          s.ReportIdentifier != null && s.ReportIdentifier !== ""
            ? String(s.ReportIdentifier)
            : null,
      }));
      return {
        well: w.Name || NA,
        samples: fmtCount(c.Total),
        sampleEntries,
        passRate: fmtCount(qc.PassRate),
        avgQualityScore: fmtCount(qc.AvgQualityScore),
        passFailPerformance: NA,
      };
    });
  }

  const CCE_OIL_VALIDATION_MATRIX_ORDER = [
    "Pressure_Monotonically_Decreases",
    "Vrel_Monotonically_Increases",
    "Vrel_Consistency_oil",
    "Vrel_Precision",
    "Vrel_Single_and_Two_Phase",
    "Vrel_Psat",
    "Vrel_Above_two",
    "VltoVtot_Reported_oil",
    "VltoVtot_Consistency_oil",
    "VltoVsat_Reported_oil",
    "VltoVsat_Consistency_oil",
    "Y_Function",
    "Y_Function_Linear",
    "Smooth_Compressibility_Curve",
    "Densities_Monotonically_Decrease",
    "Oil_Density_Range_oil",
  ];

  const CCE_OIL_DISTRIBUTION_PLOT_ORDER = [
    "OilSaturationPressure",
    "MudResFMoleFraction",
    "TestTemperature",
    "PressureStepCount",
    "RelativeVolumViolation",
    "VtotViolation",
    "YFunction",
    "OilDensityPsat",
    "OilCompressibilityPsat",
    "OilCompressibilityPres",
  ];

  const CCE_GAS_VALIDATION_MATRIX_ORDER = [
    "Pressure_Monotonically_Decreases",
    "Vrel_Monotonically_Increases",
    "Vrel_Consistency_gas",
    "Vrel_Precision",
    "Vrel_Single_and_Two_Phase",
    "Vrel_Psat",
    "Vrel_Above_two",
    "VltoVtot_Reported_gas",
    "VltoVtot_Consistency_gas",
    "VltoVsat_Reported_gas",
    "VltoVsat_Consistency_gas",
    "GasZFactor_Monotonically_Increase",
    "Oil_Density_Range_gas",
    "Densities_Monotonically_Decrease",
  ];

  const CCE_GAS_DISTRIBUTION_PLOT_ORDER = [
    "DewPoint",
    "MudResFMoleFraction",
    "TestTemperature",
    "GasZFactorPsat",
    "PressureStepCount",
    "RelativeVolumViolation",
    "GasDensityPsat",
  ];

  const DL_CONSISTENCY_CROSS_ORDER = ["saturationPressure_Consistent", "oilDensity_Consistent"];
  const DL_CONSISTENCY_ORDERING_ORDER = [
    "solutionGOR_Hierarchy",
    "oilFormationVolumeFactor_Hierarchy",
    "residualOilDensity_Hierarchy",
  ];
  const DL_EQUILIBRIUM_ORDER = ["Hofman_Linear", "Wilson_Linear", "Buckley_Linear_Negative"];
  const DL_VALIDATION_PASS_RATE_MATRIX_ORDER = [
    "GOR_Increases",
    "oilFormationVolumeFactor_Increases",
    "oilDensity_Increases",
    "gasGravity_Increases",
    "gasFormationVolumeFactor_Increases",
    "gasZFactor__aprroaches_unity",
  ];

  const DL_DISTRIBUTION_PLOT_ORDER = [
    "OilSaturationPressureDL",
    "MudResFMoleFraction",
    "TestTemperatureDL",
    "OilDensityPsatDL",
    "SolutionGOR",
    "OilFVFPsat",
    "ResidualOilDensity",
    "ΑΡΙ",
    "PressureStepCount",
  ];

  function matrixItemToRow(m) {
    const c = m.Counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failedSamples = Array.isArray(m.FailedSamples)
      ? m.FailedSamples.map((fs) => ({
          fluidSampleID: fs.FluidSampleID != null ? String(fs.FluidSampleID) : "",
          id: fs.ID != null ? String(fs.ID) : "",
        })).filter((fs) => fs.id)
      : [];
    const checkDescription = (m.Description != null
      ? String(m.Description)
      : m.description != null
        ? String(m.description)
        : ""
    ).trim();
    return {
      matrixName: m.Name || "",
      check: m.DisplayName || m.Name || NA,
      checkDescription,
      passed: fmtCount(c.Passed),
      failed: fmtCount(c.Failed),
      passRate: fmtPct(total, passed),
      failedSamples,
    };
  }

  function matrixToRows(matrix) {
    if (!Array.isArray(matrix)) return [];
    return matrix.map(matrixItemToRow);
  }

  function matrixItemToDlConsistencyStyleRow(m) {
    const c = m.Counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failedSamples = Array.isArray(m.FailedSamples)
      ? m.FailedSamples.map((fs) => ({
          fluidSampleID: fs.FluidSampleID != null ? String(fs.FluidSampleID) : "",
          id: fs.ID != null ? String(fs.ID) : "",
        })).filter((fs) => fs.id || fs.fluidSampleID)
      : [];
    return {
      displayName: m.DisplayName || m.Name || NA,
      field: m.Name || "",
      compliant: fmtCount(c.Passed),
      nonCompliant: fmtCount(c.Failed),
      complianceRate: fmtPct(total, passed),
      fluidSampleIds: [],
      failedSamples,
    };
  }

  function orderMatrixItemsByName(items, nameOrder) {
    const map = new Map(items.map((x) => [x.Name, x]));
    return nameOrder.map((n) => map.get(n)).filter(Boolean);
  }

  function splitDlValidationMatrix(matrix) {
    const cross = [];
    const ordering = [];
    const equilibrium = [];
    const reserved = new Set([
      ...DL_CONSISTENCY_CROSS_ORDER,
      ...DL_CONSISTENCY_ORDERING_ORDER,
      ...DL_EQUILIBRIUM_ORDER,
    ]);
    if (!Array.isArray(matrix)) {
      return {
        validation: [],
        cross,
        ordering,
        equilibrium,
      };
    }
    for (const m of matrix) {
      const name = m.Name || "";
      if (DL_CONSISTENCY_CROSS_ORDER.includes(name)) cross.push(m);
      else if (DL_CONSISTENCY_ORDERING_ORDER.includes(name)) ordering.push(m);
      else if (DL_EQUILIBRIUM_ORDER.includes(name)) equilibrium.push(m);
    }
    const byName = new Map(matrix.map((m) => [m.Name, m]));
    const validation = DL_VALIDATION_PASS_RATE_MATRIX_ORDER.map((n) => byName.get(n)).filter(Boolean);
    return {
      validation,
      cross: orderMatrixItemsByName(cross, DL_CONSISTENCY_CROSS_ORDER),
      ordering: orderMatrixItemsByName(ordering, DL_CONSISTENCY_ORDERING_ORDER),
      equilibrium: orderMatrixItemsByName(equilibrium, DL_EQUILIBRIUM_ORDER),
    };
  }

  function summaryFromCounts(counts) {
    const c = counts || {};
    const total = Number(c.Total) || 0;
    const passed = Number(c.Passed) || 0;
    const failed = Number(c.Failed) || 0;
    const pending = Number(c.Pending) || 0;
    const den = passed + failed + pending || total;
    return {
      overallPassRate: fmtPct(den, passed),
      passedQc: fmtCount(c.Passed),
      pendingQc: fmtCount(c.Pending),
      failed: fmtCount(c.Failed),
    };
  }

  function orderDistributionPlotsByName(plots, nameOrder) {
    if (!Array.isArray(plots) || !nameOrder.length) return [];
    const byName = new Map();
    for (const p of plots) {
      const n = String(p.Name ?? p.name ?? "").trim();
      if (n) byName.set(n, { ...p, Name: n });
    }
    return nameOrder.map((n) => byName.get(n)).filter(Boolean);
  }

  function distPlotsToOptions(plots) {
    if (!Array.isArray(plots)) return [];
    return plots.map((p) => ({
      id: String(p.Name ?? p.name ?? "").trim() || p.DisplayName || p.displayName || "",
      label: p.DisplayName || p.displayName || p.Name || p.name || "",
    }));
  }

  function distPlotToData(plot, selectedId) {
    if (!plot || plot.Name !== selectedId) return null;
    const keys = plot.DistributionPlotKeys || [];
    const categories = keys.map((k) => k.Key);
    const values = keys.map((k) => Number(k.Value) || 0);
    const total = values.reduce((a, b) => a + b, 0);
    const validKeys = keys.filter((k) => k.Key !== "N/A" && k.Key !== "Failed" && k.Key !== "Not Reported");
    const validSum = validKeys.reduce((a, k) => a + (Number(k.Value) || 0), 0);
    const outsideSum = total - validSum;
    return {
      categories,
      validRange: {
        values,
        label: "Valid Range",
        count: total === 0 ? NA : validSum,
        percent: total ? pct(total, validSum) : NA,
      },
      outsideRange: {
        count: total === 0 ? NA : outsideSum,
        label: total === 0 ? "Outside Range - not available" : (outsideSum ? `Outside Range (${outsideSum} samples) - Click to view` : "Outside Range - Click to view"),
      },
    };
  }

  const sampleIds = [];
  
  const dlConsistency = dl.ValidationPassRateMatrix || [];
  dlConsistency.forEach((item) => {
    (item.FluidSampleIDs || []).forEach((id) => {
      if (id && !sampleIds.includes(String(id))) sampleIds.push(String(id));
    });
  });
  (dl.ValidationPassRateMatrix || []).forEach((m) => {
    (m.FailedSamples || []).forEach((fs) => {
      const fid = fs.FluidSampleID != null ? String(fs.FluidSampleID) : "";
      if (fid && !sampleIds.includes(fid)) sampleIds.push(fid);
    });
  });

  return {
    summaryStats,
    qcStatusOverview,
    qcStatusPanel,
    byTab: {
      oilCce: (() => {
        const cceOilPlots = orderDistributionPlotsByName(cce.DistributionPlots, CCE_OIL_DISTRIBUTION_PLOT_ORDER);
        const cceOilMatrix = orderMatrixItemsByName(cce.ValidationPassRateMatrix || [], CCE_OIL_VALIDATION_MATRIX_ORDER);
        return {
          scorecardRows: wellsToScorecardRows(cce.Wells),
          scorecardSummary: summaryFromCounts(cceCounts),
          validationMatrixRows: matrixToRows(cceOilMatrix),
          validationMatrixTitle: "Validation Pass Rate Matrix",
          validationMatrixDescription: "Percentage of all CCE (Oil) samples passing each validation check",
          distributionPlotOptions: distPlotsToOptions(cceOilPlots),
          distributionPlots: cceOilPlots,
        };
      })(),
      gasCce: (() => {
        const cceGasPlots = orderDistributionPlotsByName(cceGas.DistributionPlots, CCE_GAS_DISTRIBUTION_PLOT_ORDER);
        const cceGasMatrix = orderMatrixItemsByName(cceGas.ValidationPassRateMatrix || [], CCE_GAS_VALIDATION_MATRIX_ORDER);
        return {
          scorecardRows: wellsToScorecardRows(cceGas.Wells),
          scorecardSummary: summaryFromCounts(cceGasCounts),
          validationMatrixRows: matrixToRows(cceGasMatrix),
          validationMatrixTitle: "Validation Pass Rate Matrix",
          validationMatrixDescription: "Percentage of all CCE (Gas) samples passing each validation check",
          distributionPlotOptions: distPlotsToOptions(cceGasPlots),
          distributionPlots: cceGasPlots,
        };
      })(),
      dlTests: (() => {
        const split = splitDlValidationMatrix(dl.ValidationPassRateMatrix);
        const dlPlotSidebarOrder = orderDistributionPlotsByName(dl.DistributionPlots, DL_DISTRIBUTION_PLOT_ORDER);
        return {
          scorecardRows: wellsToScorecardRows(dl.Wells),
          scorecardSummary: summaryFromCounts(dlCounts),
          validationMatrixRows: split.validation.map(matrixItemToRow),
          validationMatrixTitle: "Validation Pass Rate Matrix",
          validationMatrixDescription: "Percentage of all DL samples passing each validation check",
          distributionPlotOptions: distPlotsToOptions(dlPlotSidebarOrder),
          distributionPlots: dl.DistributionPlots || [],
          consistencyCrossTestRows: split.cross.map(matrixItemToDlConsistencyStyleRow),
          consistencyOrderingRows: split.ordering.map(matrixItemToDlConsistencyStyleRow),
          equilibriumPlotRows: split.equilibrium.map(matrixItemToDlConsistencyStyleRow),
        };
      })(),
    },
    sampleIds,
  };
}
